import React, { useEffect, useState, useContext } from "react";

import { Button, Tag, Collapse, Spin, Flex } from "antd";
import { instance } from "utils/axios";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "contexts";

const PROGRAM_TO_GUID = {
  1: "old-2425",
  2: "2dc88870-0577-4e2f-a12c-6904532fd6a9",
  3: "34fa515a-122a-11f1-97a1-0275a084aa75",
};

const PROGRAM_TO_YEAR = {
  1: "2024-2025",
  2: "2025-2026",
  3: "2026-2027",
};

function PaymentStatus() {
  const { Panel } = Collapse;
  const { state } = useContext(AuthContext);
  const name = state.studentInfo?.profile?.firstName;
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    setLoading(true);
    instance({
      method: "get",
      url: `/guardian/students`,
    })
      .then((res) => {
        setStudents(res.data);
      })
      .catch((err) => {
        console.log(err);
      })
      .then(() => setLoading(false));
  };

  const renderTuitionRow = (student, tuition) => {
    const guid = PROGRAM_TO_GUID[tuition.programId];
    const year = PROGRAM_TO_YEAR[tuition.programId] || `Program ${tuition.programId}`;
    const balance = Number(tuition.balance ?? 0);
    const isPaid = balance <= 0;

    return (
      // Мобайлд товчнууд доор нь бүтэн өргөнөөр — өмнө нь хажуу тийш хальж хуудсыг өргөсгөдөг байв
      <div
        key={tuition.id}
        className="flex flex-col md:flex-row md:items-center gap-3 p-4 bg-white rounded-xl mb-2 border border-[#e9e8f0]"
      >
        <div className="flex-1 min-w-0">
          <div className="font-bold flex items-center gap-2 flex-wrap">{year} хичээлийн жил{isPaid && <Tag color="success" className="!m-0">Төлөгдсөн</Tag>}</div>
          <div className="text-xs text-gray-500 mt-1 break-words">
            Үлдэгдэл: ₮{balance.toLocaleString()}
            {tuition.finAid > 0 && (
              <span className="ml-2">
                · Санхүүгийн дэмжлэг: ₮{Number(tuition.finAid).toLocaleString()}
              </span>
            )}
            {tuition.discount > 0 && (
              <span className="ml-2">
                · Хөнгөлөлт: ₮{Number(tuition.discount).toLocaleString()}
                {tuition.discountReason ? ` (${tuition.discountReason})` : ""}
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 min-[420px]:grid-cols-2 md:flex gap-2 shrink-0">
          <Button
            size="large"
            onClick={() => navigate(`/student/payment/${student.id}/${guid}`)}
          >
            Төлбөр харах
          </Button>
          <Button
            size="large"
            type="primary"
            onClick={() => navigate(`/contract/${student.id}/${guid}`)}
          >
            Гэрээ / Хавсралт
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center mt-20">
        <Flex align="center" gap="middle">
          <Spin size="large" />
        </Flex>
      </div>
    );
  }

  return (
    <div className="pt-4 md:pt-8 pb-8 max-w-3xl mx-auto w-full">
      <div className="w-full">
        <div className="w-full">
          <div className="mb-6">
            <h1 className="m-0 text-[26px] md:text-[34px] font-extrabold text-primary leading-tight">
              Сайн байна уу, {name}
            </h1>
          </div>

          {students.map((student) => {
            const tuitions = student.tuitions || [];
            const tuition3 = tuitions.find((t) => t.programId === 3);
            const tuition2 = tuitions.find((t) => t.programId === 2);
            const tuition1 = tuitions.find((t) => t.programId === 1);
            const hasPermission = student.permission == 1;

            return (
              <div
                key={student.id}
                className="mb-8"
              >
                {/* Хүүхдийн нэр */}
                <div className="text-lg font-bold text-primary mb-4">
                  {student.firstName} {student.lastName}
                </div>

                {/* ─── 2026-2027: эрхтэй бол tuition, эрхгүй бол мессеж ─── */}
                {hasPermission ? (
                  tuition3 && (
                    <div className="mb-6 p-4 md:p-6 border-2 border-primary rounded-[18px] bg-primary/5">
                      <div className="text-xl font-bold text-primary mb-4">
                        2026–2027 хичээлийн жил
                      </div>
                      <div className="mb-4 leading-[1.38]">
                        Та хүүхдийнхээ Томүжин Алтернатив сургуульд 2026–2027 оны
                        хичээлийн жилд үргэлжлүүлэн суралцахтай холбоотой
                        сургалтын төлбөрийн мэдээлэл, суудал баталгаажуулалт
                        болон гэрээний явцтай танилцана уу.
                      </div>
                      {renderTuitionRow(student, tuition3)}
                    </div>
                  )
                ) : (
                  <div className="mb-6 p-4 md:p-6 border-2 border-orange-400 rounded-[18px] bg-orange-50">
                    <div className="text-xl font-bold text-orange-600 mb-4">
                      2026–2027 хичээлийн жил
                    </div>
                    <div className="leading-[1.38]">
                      Таны хүүхдийн 2026–2027 оны хичээлийн жилд үргэлжлүүлэн
                      суралцах эрх одоогоор үүсээгүй байна. Сургалтын албатай
                      холбогдоно уу.
                    </div>
                  </div>
                )}

                {/* ─── Хуучин жилүүд — Collapse ─── */}
                {(tuition2 || tuition1) && (
                  <div className="mt-4 w-full">
                    <Collapse
                      accordion
                      bordered={false}
                      className="custom-collapse"
                    >
                      {tuition2 && (
                        <Panel
                          header="2025–2026 хичээлийн жилийн мэдээллийг харах"
                          key="2"
                        >
                          {renderTuitionRow(student, tuition2)}
                        </Panel>
                      )}

                      {tuition1 && (
                        <Panel
                          header="2024–2025 хичээлийн жилийн мэдээллийг харах"
                          key="1"
                        >
                          {renderTuitionRow(student, tuition1)}
                        </Panel>
                      )}
                    </Collapse>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PaymentStatus;